import React, { useEffect, useState, useCallback } from 'react';
import { useRecoilValue } from 'recoil';
import { classesAtom, currentTermAtom } from '_state';
import { useStudentScoreAction } from '_actions';
import { useClassWrapper } from '_helpers';
import { useClassActions } from '_actions';
import { computeSkillGPA } from '_helpers/gpa';
import { Scoreboard } from '_components/studentScoreList';

const SKILLS_ORDER = ['READING', 'LISTENING', 'WRITING', 'SPEAKING'];

export { StudentScore };

function StudentScore(props) {
    const studentScoreAction = useStudentScoreAction();
    const classWrapper = useClassWrapper();
    const classes = useRecoilValue(classesAtom);

    const classActions = useClassActions();
    const currentTerm = useRecoilValue(currentTermAtom);

    // Populate classesAtom if the student landed here without visiting Home first
    useEffect(() => {
        if (props.isPersonal && classes.length === 0) classActions.getClassList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.isPersonal]);

    const defaultClassId = classWrapper.curClass?.class_id;
    const [viewClassId, setViewClassId] = useState(defaultClassId);
    useEffect(() => { setViewClassId(defaultClassId); }, [defaultClassId]);

    // Which class this board is showing: the staff modal passes class_id, the personal page tracks
    // viewClassId. A trial class shows a single "Điểm đầu vào" column instead of the term columns.
    const shownClassId = props.isPersonal ? viewClassId : props.class_id;
    const isTrial = !!(classes.find(c => c.class_id === shownClassId)?.is_trial
        ?? (shownClassId === defaultClassId ? classWrapper.curClass?.is_trial : false));

    const [scoreboard, setScoreboard] = useState([]);
    const [scoreTotal, setScoreTotal] = useState({});
    const [rawData, setRawData] = useState(null);

    const computeScoreboard = useCallback((data) => {
        if (!data || !data.scores) return;

        const skillMap = {};
        data.scores.forEach(element => {
            const skillCode = element.subject?.subject_code || 'UNKNOWN';
            const skillName = element.subject?.subject_name || skillCode;
            if (!skillMap[skillCode]) {
                skillMap[skillCode] = { subject_name: skillName, subject_code: skillCode, tests: [], midterm: null, final: null };
            }
            const st = element.score_type || 'final';
            if (st === 'midterm') {
                skillMap[skillCode].midterm = { score: element.score, score_id: element._id };
            } else if (st === 'final') {
                skillMap[skillCode].final = { score: element.score, score_id: element._id };
            } else {
                skillMap[skillCode].tests.push({ score: element.score, score_id: element._id, score_type: st });
            }
        });

        Object.values(skillMap).forEach(skill => {
            skill.tests.sort((a, b) => {
                const aNum = parseInt(a.score_type.replace('test_', '')) || 0;
                const bNum = parseInt(b.score_type.replace('test_', '')) || 0;
                return aNum - bNum;
            });
        });

        let overallTotal = 0, skillCount = 0;
        const order = [...SKILLS_ORDER, ...Object.keys(skillMap).filter(k => !SKILLS_ORDER.includes(k))];
        const rows = order
            .filter(code => skillMap[code])
            .map(code => {
                const skill = skillMap[code];
                const midtermScore = skill.midterm ? skill.midterm.score : null;
                const finalScore   = skill.final   ? skill.final.score   : null;
                const testScores   = skill.tests.map(t => t.score);
                const gpa = computeSkillGPA(testScores, midtermScore, finalScore);
                if (gpa !== null) { overallTotal += gpa; skillCount++; }
                return {
                    key: code,
                    subject_name: skill.subject_name,
                    subject_code: code,
                    tests: skill.tests,
                    midterm: skill.midterm,
                    final: skill.final,
                    gpa: gpa !== null ? gpa.toFixed(3) : '--',
                };
            });

        const overallGPA = skillCount > 0 ? overallTotal / skillCount : 0;
        const overallStr = overallGPA.toFixed(3);
        const hasScores = data.scores && data.scores.length > 0;
        const state = !hasScores ? ['Chưa có điểm']
            : overallGPA >= 5.0 ? ['Bình thường']
            : overallGPA >= 2.5 ? ['Cảnh cáo']
            : ['Đuổi học'];

        setScoreboard(rows);
        setScoreTotal({
            name: data.user_ref?.name || '',
            student_id: data.user_ref?.student_id || '',
            gpa: overallStr,
            stt: state,
        });
    }, []);

    useEffect(() => {
        if (rawData) computeScoreboard(rawData);
    }, [rawData, computeScoreboard]);

    const loadScores = useCallback(async () => {
        if (!props.student_id && !props.isPersonal) return;
        let newData = props.isPersonal === true
            ? await studentScoreAction.getMyScore(viewClassId || undefined)
            : await studentScoreAction.getScoreByID(props.student_id, null, props.class_id);
        if (!newData) return;
        setRawData(newData);
    }, [props.student_id, props.isPersonal, props.class_id, viewClassId]);

    useEffect(() => { loadScores(); }, [loadScores]);

    // Staff-only manual score entry. student_id/class_id come from the props the
    // staff score modal passes in; term follows the globally selected term (if any).
    const handleAddScore = useCallback(async ({ subject_code, score, score_type }) => {
        return await studentScoreAction.addScore({
            student_id: props.student_id,
            class_id: props.class_id,
            term_id: currentTerm || undefined,
            subject_code,
            score,
            score_type,
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.student_id, props.class_id, currentTerm]);

    return (
        <div className="soft-in">
            <Scoreboard
                scoreboard={scoreboard}
                scoreTotal={scoreTotal}
                isPersonal={props.isPersonal}
                isTrial={isTrial}
                onUpdateScore={studentScoreAction.updateScore}
                onAddScore={props.isPersonal === false ? handleAddScore : undefined}
                onRefresh={loadScores}
            />
        </div>
    );
}
